import React, { useEffect, useMemo, useState } from 'react';
import { disputeService, PreviaDasPresas } from '../../services/disputeService';
import CascaDeModal from '../shared/CascaDeModal';
import { acaoPrincipal, acaoSecundaria, falhaIgnoradaDeProposito } from '../../design';
import { fmtDateTime } from '../../utils/formatters';
import { TONE_TEXT } from '../../utils/statusColors';

/**
 * ENCERRAR SESSÕES PRESAS (backend #533, 23/10/2026). Sessão de disputa que ficou "configurando" ou "pausada" depois de a
 * licitação acabar polui o Disputar para sempre. A regra é do backend (três pernas, com a frase de cada uma); a tela só
 * mostra a prévia e manda os ids que o Heitor marcou.
 *
 * - O botão é discreto e SÓ aparece com N > 0. Sem permissão (não-admin) ou com o backend antigo, a prévia falha e o botão
 *   simplesmente não existe: não é uma ação que alguém espere ver.
 * - Nada vem marcado: quem confirma escolhe linha por linha.
 * - Havendo RECUSA (sessão com sinal de vida alcançada pela regra), o backend aborta a execução inteira; a tela mostra o
 *   porquê e não oferece o botão.
 * - O aviso fixo vem do backend ("resultado e ranking gravados ficam"): é a mesma promessa que a escrita cumpre.
 * - Por linha: id, licitação, desde quando parada e a regra em texto (do backend). O `motivo` é do activity_log e não entra.
 */
const diasDesde = (iso: string | null) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

export default function EncerrarSessoesPresas({ onEncerradas, avisar }: {
  onEncerradas: () => void;
  avisar: (mensagem: string, tipo?: 'success' | 'error') => void;
}) {
  const [previa, setPrevia] = useState<PreviaDasPresas | null>(null);
  const [aberto, setAberto] = useState(false);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ encerradas: number; recusados: Array<{ id: number; motivo: string }> } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const ler = async () => {
    try {
      setPrevia(await disputeService.previaDasPresas());
    } catch (e) {
      falhaIgnoradaDeProposito('sem permissão (não-admin) ou backend sem a rota: o botão simplesmente não aparece; não é ação que alguém espere ver')(e);
      setPrevia(null);
    }
  };
  useEffect(() => { ler(); }, []);

  const linhas = useMemo(() => previa?.encerrar || [], [previa]);
  const recusas = previa?.recusas || [];
  if (!previa || linhas.length === 0) return null;

  const abrir = () => { setMarcados(new Set()); setResultado(null); setErro(null); setAberto(true); ler(); };
  const alternar = (id: number) => setMarcados((m) => { const n = new Set(m); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const confirmar = async () => {
    setEnviando(true); setErro(null);
    try {
      const r = await disputeService.encerrarPresas(Array.from(marcados));
      setResultado({ encerradas: r.encerradas, recusados: r.recusados || [] });
      if (r.encerradas > 0) { avisar(`${r.encerradas} ${r.encerradas === 1 ? 'sessão encerrada' : 'sessões encerradas'}`); onEncerradas(); }
    } catch (e: any) {
      setErro(e?.response?.data?.message || e?.message || 'o servidor não respondeu');
    } finally { setEnviando(false); }
  };
  const fechar = () => { setAberto(false); if (resultado) ler(); };
  const licitacaoDe = (id: number) => linhas.find((l) => l.id === id)?.licitacao || `#${id}`;

  return (
    <>
      <button type='button' onClick={abrir} data-encerrar-presas
        className='px-3 py-2 text-sm text-slate-400 hover:text-white rounded-lg border border-transparent hover:border-central-borda transition-colors'
        title='Sessões de disputa que ficaram abertas depois de a licitação acabar'>
        Encerrar sessões presas ({linhas.length})
      </button>
      {aberto && (
        <CascaDeModal
          largura='lg'
          titulo='Encerrar sessões presas'
          onClose={fechar}
          corpoClassName='p-5 space-y-4'
          rodape={
            <div className='ml-auto flex gap-3'>
              <button type='button' onClick={fechar} className={`px-4 py-2 text-sm rounded-lg ${acaoSecundaria}`}>{resultado ? 'Fechar' : 'Cancelar'}</button>
              {!resultado && recusas.length === 0 && (
                <button type='button' onClick={confirmar} disabled={enviando || marcados.size === 0} data-confirmar-presas
                  className={`px-4 py-2 text-sm rounded-lg disabled:opacity-50 ${acaoPrincipal}`}>
                  {enviando ? 'Encerrando…' : `Encerrar ${marcados.size} ${marcados.size === 1 ? 'sessão' : 'sessões'}`}
                </button>
              )}
            </div>
          }
        >
          <p className='text-sm text-central-texto' data-aviso-fixo>{previa.avisoFixo}</p>
          {recusas.length > 0 && (
            <div role='alert' className='rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200' data-recusas>
              <p className='font-medium'>O servidor não vai encerrar nada agora: {recusas.length === 1 ? 'uma sessão alcançada pela regra mostra' : `${recusas.length} sessões alcançadas pela regra mostram`} sinal de vida.</p>
              <ul className='list-disc pl-5 mt-1'>{recusas.map((r) => <li key={r.id}>#{r.id} {r.licitacao}: {r.recusa}</li>)}</ul>
            </div>
          )}
          {resultado ? (
            <div className='space-y-2 text-sm' data-resultado-presas>
              <p className='text-central-texto'>{resultado.encerradas} {resultado.encerradas === 1 ? 'sessão encerrada' : 'sessões encerradas'}.</p>
              {resultado.recusados.length > 0 && (
                <div className={TONE_TEXT.warning}>
                  <p className='font-medium'>Não encerradas:</p>
                  <ul className='list-disc pl-5'>{resultado.recusados.map((r) => <li key={r.id}>#{r.id} {licitacaoDe(r.id)}: {r.motivo}</li>)}</ul>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className='text-xs text-slate-500'>Nada vem marcado. Marque as que de fato terminaram; só elas vão para o servidor.</p>
              <div className='divide-y divide-central-borda border border-central-borda rounded-central'>
                {linhas.map((l) => {
                  const dias = diasDesde(l.paradaDesde);
                  return (
                    <label key={l.id} data-presa={l.id} className='flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-central-elevado'>
                      <input type='checkbox' className='mt-1' checked={marcados.has(l.id)} onChange={() => alternar(l.id)} aria-label={`Encerrar a sessão ${l.id}`} />
                      <div className='min-w-0 flex-1 text-sm'>
                        <div className='flex flex-wrap items-baseline gap-x-2'>
                          <span className='text-slate-500 tabular-nums'>#{l.id}</span>
                          <span className='text-central-texto font-medium'>{l.licitacao}</span>
                        </div>
                        <div className='text-xs text-slate-400 mt-0.5'>
                          {l.paradaDesde ? `parada desde ${fmtDateTime(l.paradaDesde)}${dias !== null ? ` (há ${dias} ${dias === 1 ? 'dia' : 'dias'})` : ''}` : 'sem data de parada'}
                          {l.regraEmTexto && <> · {l.regraEmTexto}</>}
                        </div>
                        {/* O `motivo` do backend fica fora da tela de propósito: é a frase do activity_log, com a data em ISO
                            UTC e o status entre crases. "Parada desde" já diz a data em Brasília, e a regra vem em texto. */}
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
          {erro && <p role='alert' className='text-sm text-red-300'>Não encerrou: {erro}</p>}
        </CascaDeModal>
      )}
    </>
  );
}
